import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef, DataTable } from '~/components/composites/data-table'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select'
import { useDebounce } from '~/hooks/use-debounce'
import { useQuery } from '~/hooks/use-query'
import { useAppStore } from '~/stores/app'
import { DEFAULT_PAGE_SIZE, DialogId, type ListResponse } from '~/types/common'
import { QueryKey } from '~/types/query-key'
import type { Skill } from '~/types/skill'

type SkillsListProps = {
  onViewDetail: (item: Skill) => void
}

export function SkillsList({ onViewDetail }: SkillsListProps) {
  const { t } = useTranslation()
  const openDialog = useAppStore((state) => state.openDialog)

  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(search)

  const queries: Record<string, unknown> = {
    page,
    limit: DEFAULT_PAGE_SIZE,
  }
  if (debouncedSearch) queries.search = debouncedSearch
  if (activeFilter !== 'all') queries.isActive = activeFilter === 'true'
  if (categoryFilter !== 'all') queries.category = categoryFilter

  const { data, isLoading } = useQuery<ListResponse<Skill>>({
    queryKey: QueryKey.SKILLS,
    queries,
  })

  const handleRowClick = (item: Skill) => {
    onViewDetail(item)
    openDialog(DialogId.DETAIL_SKILL)
  }

  const columns: ColumnDef<Skill>[] = [
    {
      key: 'icon',
      header: t('skills.icon'),
      render: (item) =>
        item.iconUrl ? (
          <img src={item.iconUrl} alt="" className="h-8 w-8 rounded object-cover" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded bg-muted text-[10px] text-muted-foreground">
            {t('skills.noIcon')}
          </div>
        ),
      className: 'w-[64px]',
    },
    {
      key: 'nameEn',
      header: t('skills.nameEn'),
      render: (item) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{item.nameEn}</span>
          {item.schoolId && <Badge variant="outline">{t('skills.syncedFromSchools')}</Badge>}
        </span>
      ),
    },
    {
      key: 'nameJa',
      header: t('skills.nameJa'),
      render: (item) => item.nameJa,
    },
    {
      key: 'category',
      header: t('skills.category'),
      render: (item) => (item.category ? t(`skillCategory.${item.category.slug}`) : '-'),
      className: 'w-[120px]',
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (item) => (
        <Badge variant={item.isActive ? 'success' : 'secondary'}>
          {item.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
      className: 'w-[100px]',
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      render: (item) => new Date(item.createdAt).toLocaleDateString(),
      className: 'w-[150px]',
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-4">
        <div className="w-full max-w-sm space-y-1.5">
          <Label htmlFor="skills-search">{t('common.search')}</Label>
          <Input
            id="skills-search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="skills-category-filter">{t('skills.category')}</Label>
          <Select
            value={categoryFilter}
            onValueChange={(value) => {
              setCategoryFilter(value)
              setPage(1)
            }}
          >
            <SelectTrigger id="skills-category-filter" className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="sports">{t('skillCategory.sports')}</SelectItem>
              <SelectItem value="skill">{t('skillCategory.skill')}</SelectItem>
              <SelectItem value="school">{t('skillCategory.school')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="skills-status-filter">{t('common.status')}</Label>
          <Select
            value={activeFilter}
            onValueChange={(value) => {
              setActiveFilter(value)
              setPage(1)
            }}
          >
            <SelectTrigger id="skills-status-filter" className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              <SelectItem value="true">{t('common.active')}</SelectItem>
              <SelectItem value="false">{t('common.inactive')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button id="skills-add-button" className="ml-auto" onClick={() => openDialog(DialogId.CREATE_SKILL)}>
          {t('skills.addNew')}
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        pagination={data?.pagination}
        onPageChange={setPage}
        onRowClick={handleRowClick}
      />
    </div>
  )
}
